import axios from 'axios';
import { API_BASE_URL } from '../config';

// Intercepteur pour ajouter le token JWT
const api = axios.create({
  baseURL: API_BASE_URL,
});

// Intercepteur pour les requêtes
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Intercepteur pour les réponses
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (refreshToken) {
          const response = await axios.post(`${API_BASE_URL}/auth/token/refresh/`, {
            refresh: refreshToken,
          });

          const { access } = response.data;
          localStorage.setItem('access_token', access);

          originalRequest.headers.Authorization = `Bearer ${access}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Interfaces TypeScript
export interface RendezVous {
  id: number;
  proprietaire: number;
  veterinaire: number;
  animal: number;
  date_heure: string;
  duree_minutes: number;
  motif: string;
  statut: 'demande' | 'confirme' | 'annule' | 'termine';
  notes_proprietaire?: string;
  notes_veterinaire?: string;
  date_creation: string;
  date_modification: string;
  proprietaire_nom?: string;
  veterinaire_nom?: string;
  animal_nom?: string;
  statut_display?: string;
}

export interface CreateRendezVousData {
  veterinaire: number;
  animal: number;
  date_heure: string;
  motif: string;
  notes_proprietaire?: string;
}

export interface CreneauDisponible {
  date_heure: string;
  veterinaire_id: number;
  veterinaire_nom: string;
  duree_minutes: number;
}

export interface RendezVousListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: RendezVous[];
}

export interface DisponibiliteVeterinaire {
  id: number;
  veterinaire: number;
  jour_semaine: number;
  heure_debut: string;
  heure_fin: string;
  actif: boolean;
  date_creation: string;
  veterinaire_nom?: string;
  jour_semaine_display?: string;
}

export interface IndisponibiliteVeterinaire {
  id: number;
  veterinaire: number;
  date_debut: string;
  date_fin: string;
  type_indisponibilite: 'conges' | 'formation' | 'urgence' | 'personnel' | 'autre';
  motif?: string;
  date_creation: string;
  veterinaire_nom?: string;
  type_display?: string;
}

const rendezVousService = {
  // === GESTION DES RENDEZ-VOUS ===

  // Récupérer tous les rendez-vous (selon le rôle)
  async getRendezVous(params?: {
    page?: number;
    page_size?: number;
    veterinaire?: number;
    statut?: string;
    animal?: number;
    search?: string;
  }): Promise<RendezVousListResponse> {
    const response = await api.get('/rendez-vous/', { params });
    return response.data;
  },

  // Créer un nouveau rendez-vous
  async createRendezVous(data: CreateRendezVousData): Promise<RendezVous> {
    const response = await api.post('/rendez-vous/', data);
    return response.data;
  },

  // Récupérer un rendez-vous par ID
  async getRendezVousById(id: number): Promise<RendezVous> {
    const response = await api.get(`/rendez-vous/${id}/`);
    return response.data;
  },

  // Mettre à jour un rendez-vous
  async updateRendezVous(id: number, data: Partial<CreateRendezVousData>): Promise<RendezVous> {
    const response = await api.patch(`/rendez-vous/${id}/`, data);
    return response.data;
  },

  // Supprimer un rendez-vous
  async deleteRendezVous(id: number): Promise<void> {
    await api.delete(`/rendez-vous/${id}/`);
  },

  // Confirmer un rendez-vous (vétérinaire)
  async confirmerRendezVous(id: number): Promise<{ status: string }> {
    const response = await api.post(`/rendez-vous/${id}/confirmer/`);
    return response.data;
  },

  // Annuler un rendez-vous
  async annulerRendezVous(id: number): Promise<{ status: string }> {
    const response = await api.post(`/rendez-vous/${id}/annuler/`);
    return response.data;
  },

  // Obtenir le planning du vétérinaire
  async getPlanning(params?: {
    date_debut?: string;
    date_fin?: string;
  }): Promise<RendezVous[]> {
    const response = await api.get('/rendez-vous/planning/', { params });
    return response.data;
  },

  // Obtenir les créneaux disponibles
  async getCreneauxDisponibles(veterinaire_id: number, date: string): Promise<CreneauDisponible[]> {
    if (!date || !veterinaire_id) {
      throw new Error('Date et ID vétérinaire requis');
    }
    
    const response = await api.get('/rendez-vous/creneaux_disponibles/', {
      params: { veterinaire_id, date }
    });
    return response.data;
  },

  // === GESTION DES DISPONIBILITÉS ===

  // Récupérer les disponibilités
  async getDisponibilites(params?: {
    veterinaire?: number;
    jour_semaine?: number;
    actif?: boolean;
  }): Promise<DisponibiliteVeterinaire[]> {
    const response = await api.get('/disponibilites/', { params });
    return response.data.results || response.data;
  },

  // Créer une disponibilité
  async createDisponibilite(data: {
    jour_semaine: number;
    heure_debut: string;
    heure_fin: string;
    actif?: boolean;
  }): Promise<DisponibiliteVeterinaire> {
    const response = await api.post('/disponibilites/', data);
    return response.data;
  },

  // Mettre à jour une disponibilité
  async updateDisponibilite(id: number, data: Partial<{
    jour_semaine: number;
    heure_debut: string;
    heure_fin: string;
    actif: boolean;
  }>): Promise<DisponibiliteVeterinaire> {
    const response = await api.patch(`/disponibilites/${id}/`, data);
    return response.data;
  },

  // Supprimer une disponibilité
  async deleteDisponibilite(id: number): Promise<void> {
    await api.delete(`/disponibilites/${id}/`);
  },

  // Obtenir le planning hebdomadaire du vétérinaire connecté
  async getMaSemaine(): Promise<{ [jour: string]: any[] }> {
    const response = await api.get('/disponibilites/ma_semaine/');
    return response.data;
  },

  // Définir un planning type
  async definirPlanningType(planning: {
    [jour: string]: { debut: string; fin: string; duree_minutes?: number }[];
  }): Promise<{ status: string; planning: DisponibiliteVeterinaire[] }> {
    const response = await api.post('/disponibilites/definir_planning_type/', {
      planning
    });
    return response.data;
  },

  // === GESTION DES INDISPONIBILITÉS ===

  // Récupérer les indisponibilités
  async getIndisponibilites(params?: {
    veterinaire?: number;
    type_indisponibilite?: string;
    search?: string;
  }): Promise<IndisponibiliteVeterinaire[]> {
    const response = await api.get('/indisponibilites/', { params });
    return response.data.results || response.data;
  },

  // Créer une indisponibilité
  async createIndisponibilite(data: {
    date_debut: string;
    date_fin: string;
    type_indisponibilite: string;
    motif?: string;
  }): Promise<IndisponibiliteVeterinaire> {
    const response = await api.post('/indisponibilites/', data);
    return response.data;
  },

  // Mettre à jour une indisponibilité
  async updateIndisponibilite(id: number, data: Partial<{
    date_debut: string;
    date_fin: string;
    type_indisponibilite: string;
    motif: string;
  }>): Promise<IndisponibiliteVeterinaire> {
    const response = await api.patch(`/indisponibilites/${id}/`, data);
    return response.data;
  },

  // Supprimer une indisponibilité
  async deleteIndisponibilite(id: number): Promise<void> {
    await api.delete(`/indisponibilites/${id}/`);
  },

  // Obtenir les indisponibilités futures
  async getIndisponibilitesFutures(veterinaire_id?: number): Promise<IndisponibiliteVeterinaire[]> {
    const response = await api.get('/indisponibilites/futures/', {
      params: veterinaire_id ? { veterinaire_id } : {}
    });
    return response.data;
  },

  // Obtenir les indisponibilités pour une période
  async getIndisponibilitesPeriode(
    date_debut: string,
    date_fin: string,
    veterinaire_id?: number
  ): Promise<IndisponibiliteVeterinaire[]> {
    const response = await api.get('/indisponibilites/periode/', {
      params: {
        date_debut,
        date_fin,
        ...(veterinaire_id && { veterinaire_id })
      }
    });
    return response.data;
  },

  // === UTILITAIRES ===

  // Formater une date pour l'API
  formatDateForAPI(date: Date): string {
    return date.toISOString().split('T')[0];
  },

  // Formater une date et heure pour l'API
  formatDateTimeForAPI(date: Date): string {
    return date.toISOString();
  },

  // Parser une date de l'API
  parseAPIDate(dateString: string): Date {
    return new Date(dateString);
  },

  // Générer les créneaux de 30 minutes entre deux heures
  genererCreneaux(heureDebut: string, heureFin: string): string[] {
    const creneaux: string[] = [];
    const debut = new Date(`2000-01-01T${heureDebut}`);
    const fin = new Date(`2000-01-01T${heureFin}`);
    
    let courant = new Date(debut);
    while (courant < fin) {
      creneaux.push(courant.toTimeString().slice(0, 5));
      courant.setMinutes(courant.getMinutes() + 30);
    }
    
    return creneaux;
  },

  // Obtenir le nom du jour de la semaine
  getJourSemaine(index: number): string {
    const jours = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
    return jours[index] || '';
  },

  // Obtenir les statuts de rendez-vous
  getStatuts(): { value: string; label: string; color: string }[] {
    return [
      { value: 'demande', label: 'Demande', color: 'warning' },
      { value: 'confirme', label: 'Confirmé', color: 'success' },
      { value: 'annule', label: 'Annulé', color: 'error' },
      { value: 'termine', label: 'Terminé', color: 'info' }
    ];
  },

  // Obtenir les types d'indisponibilité
  getTypesIndisponibilite(): { value: string; label: string; color: string }[] {
    return [
      { value: 'conges', label: 'Congés', color: 'primary' },
      { value: 'formation', label: 'Formation', color: 'info' },
      { value: 'urgence', label: 'Urgence', color: 'error' },
      { value: 'personnel', label: 'Personnel', color: 'warning' },
      { value: 'autre', label: 'Autre', color: 'default' }
    ];
  }
};

export default rendezVousService; 